import { inject, provide, reactive, type InjectionKey } from 'vue'

export interface PageHeaderAction {
  label?: string
  icon?: string | string[]
  handler: (event?: Event) => void | Promise<void>
  loading?: boolean
  disabled?: boolean
}

export interface PageHeaderButton {
  kind: 'button'
  key?: string
  label?: string
  icon?: string | string[]
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast'
  outlined?: boolean
  handler: (event: Event) => void | Promise<void>
  disabled?: boolean
  loading?: boolean
}

export interface PageHeaderSelect {
  kind: 'select'
  key?: string
  label?: string
  options: Array<{ label: string; value: string | number | boolean }>
  value: string | number | boolean
  onChange: (value: string | number | boolean) => void
  placeholder?: string
  disabled?: boolean
  optionLabel?: string
  optionValue?: string
}

export interface PageHeaderInput {
  kind: 'input'
  key?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  icon?: string | string[]
}

export type PageHeaderControl = PageHeaderButton | PageHeaderSelect | PageHeaderInput

/**
 * Tab entry. A tab can either be route-based (uses router-link) or value-based
 * (a content-switch tab that fires `onSelect` when clicked).
 */
export interface PageHeaderTab {
  label: string
  icon?: string | string[]
  route?: string
  value?: string | number
  active?: boolean
  onSelect?: (value: string | number) => void
}

interface PageHeaderState {
  title: string | null
  save: PageHeaderAction | null
  reset: PageHeaderAction | null
  dirty: boolean
  tabs: PageHeaderTab[]
  actions: PageHeaderControl[]
}

export interface PageHeaderApi {
  state: PageHeaderState
  setTitle: (title: string | null) => void
  setSave: (action: PageHeaderAction | null) => void
  setReset: (action: PageHeaderAction | null) => void
  setDirty: (value: boolean) => void
  setTabs: (tabs: PageHeaderTab[]) => void
  setActions: (actions: PageHeaderControl[]) => void
  clear: () => void
}

const PAGE_HEADER_KEY: InjectionKey<PageHeaderApi> = Symbol('pageHeader')

export function providePageHeader(): PageHeaderApi {
  const state = reactive<PageHeaderState>({
    title: null,
    save: null,
    reset: null,
    dirty: false,
    tabs: [],
    actions: []
  })

  const api: PageHeaderApi = {
    state,
    setTitle: (title) => {
      state.title = title
    },
    setSave: (action) => {
      state.save = action
    },
    setReset: (action) => {
      state.reset = action
    },
    setDirty: (value) => {
      state.dirty = value
    },
    setTabs: (tabs) => {
      state.tabs = tabs
    },
    setActions: (actions) => {
      state.actions = actions
    },
    clear: () => {
      state.title = null
      state.save = null
      state.reset = null
      state.dirty = false
      state.tabs = []
      state.actions = []
    }
  }

  provide(PAGE_HEADER_KEY, api)
  return api
}

export function usePageHeader(): PageHeaderApi {
  const api = tryUsePageHeader()
  if (!api) {
    throw new Error(
      'usePageHeader() must be used inside a component that provides it (BackendTemplate).'
    )
  }
  return api
}

export function tryUsePageHeader(): PageHeaderApi | null {
  return inject(PAGE_HEADER_KEY, null)
}
